import { useState } from "react";
import { Card } from "@components/cards/card";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import {
  FaTicketAlt,
  FaUserAlt,
  FaTag,
  FaMoneyBillWave,
  FaDollarSign,
  FaEye,
  FaEyeSlash,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { Masks } from "@hooks/functions/Masks/Masks";
import dayjs from "dayjs";
import { formatName } from "@hooks/functions/formatName";
import { Link } from "react-router-dom";
import { config } from "config";

interface IProps {
  raffle: IRaffleDefaultDTO;
  title?: string;
}

interface IShow {
  campaign: boolean;
  description: boolean;
}

const masks = new Masks();

export const RafflePrimaryInfoVisitor = ({ raffle, title }: IProps) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [show, setShow] = useState<IShow>({
    campaign: true,
    description: false,
  });

  const raffleStatus = raffle ? getRaffleStatus(raffle.status) : null;

  const initials =
    raffle?.customerData?.name
      ?.split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase() ?? "??";

  return (
    <Card
      className="visit-info overflow-hidden border"
      style={{ borderColor: "#e8eaed", background: "#fff" }}
    >
      {/* ── HEADER ── */}
      <div
        className="px-4 py-3 d-flex justify-content-between align-items-center"
        style={{
          background: "#fff",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setShow((p) => ({ ...p, campaign: !p.campaign }))}
      >
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <h5
              className="mb-0 fw-bold"
              style={{
                color: "#151f44",
                fontFamily: "'Sora', sans-serif",
                fontSize: 17,
              }}
            >
              {title || ""}
              Informações da campanha
            </h5>
            {raffle?.campaign && (
              <span
                className="px-2 py-1 rounded-2"
                style={{
                  background: "#f1f6ff",
                  border: "1px solid #dce8f8",
                  color: "#466385",
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: "monospace",
                }}
              >
                #{raffle.campaign}
              </span>
            )}
          </div>
          <p className="mb-0" style={{ color: "#6c7d98", fontSize: 13 }}>
            {raffle ? raffle.description_short : "Resumo da Campanha"}
          </p>
        </div>

        <div className="d-flex align-items-center gap-2 ms-3 flex-shrink-0">
          {raffle && raffleStatus && (
            <span
              className="rounded-pill px-3 py-1"
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                background: `rgba(var(--bs-${raffleStatus.color}-rgb), 0.15)`,
                color: `var(--bs-${raffleStatus.color})`,
                border: `1px solid rgba(var(--bs-${raffleStatus.color}-rgb), 0.3)`,
              }}
            >
              {raffleStatus.text}
            </span>
          )}
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: 32,
              height: 32,
              background: "#f1f6ff",
              border: "1px solid #dce8f8",
              color: "#315581",
            }}
          >
            {show.campaign ? (
              <FaChevronUp size={12} />
            ) : (
              <FaChevronDown size={12} />
            )}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      {show.campaign && (
        <div className="p-4" style={{ background: "#fff" }}>
          {!raffle ? (
            <LoadingSkeleton height={200} count={1} />
          ) : (
            <>
              {/* Owner */}
              <p
                className="mb-2"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Proprietário da Campanha
              </p>
              <Link
                to={`${config.urls.panel}/profile?atsign=${raffle.customerData.atSign}`}
                target="_blank"
                className="d-flex align-items-center gap-3 text-decoration-none p-3 rounded-4 mb-4 border"
                style={{
                  background: "#fff",
                  borderColor: "#e8eaed",
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = "#a78bfa")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = "#e8eaed")
                }
              >
                {raffle.customerData.avatar ? (
                  <img
                    src={raffle.customerData.avatar}
                    alt={formatName(raffle.customerData.name || "")}
                    className="rounded-circle flex-shrink-0"
                    style={{ width: 48, height: 48, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white flex-shrink-0 bg-secondary"
                    style={{ width: 48, height: 48, fontSize: 16 }}
                  >
                    {initials}
                  </div>
                )}
                <div>
                  <div
                    className="fw-bold"
                    style={{
                      color: "#0f172a",
                      fontSize: 15,
                      fontFamily: "'Sora', sans-serif",
                    }}
                  >
                    {formatName(raffle.customerData.name || "")}
                  </div>
                  <div className="d-inline-block bg-secondary rounded text-white px-2 py-1 mt-1 fw-bold" style={{ fontSize: 13 }}>
                    @{raffle.customerData.atSign}
                  </div>
                </div>
              </Link>

              {/* Metrics grid */}
              <p
                className="mb-2"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Detalhes da Campanha
              </p>
              <div className="row g-2 mb-4">
                {[
                  {
                    icon: <FaTag />,
                    bg: "#ede9fe",
                    color: "#7c3aed",
                    label: "Campanha",
                    value: raffle.name,
                  },
                  {
                    icon: <FaTicketAlt />,
                    bg: "#dbeafe",
                    color: "#1d4ed8",
                    label: "Tipo de Ticket",
                    value: raffle?.typeData?.name,
                  },
                  {
                    icon: <FaTicketAlt />,
                    bg: "#cffafe",
                    color: "#0891b2",
                    label: "Total de Tickets",
                    value: masks.formatMilhar(raffle.quantity || 0),
                  },
                  {
                    icon: <FaMoneyBillWave />,
                    bg: "#fef3c7",
                    color: "#b45309",
                    label: "Preço Unitário",
                    value: masks.brlCurrency(raffle.price),
                  },
                  {
                    icon: <FaDollarSign />,
                    bg: "#dcfce7",
                    color: "#16a34a",
                    label: "Premiação Total",
                    value: masks.brlCurrency(raffle.rewardPriceMarket),
                    valueColor: "#16a34a",
                  },
                  {
                    icon: <FaUserAlt />,
                    bg: "#f1f5f9",
                    color: "#475569",
                    label: "Slug",
                    value: `#${raffle.campaign}`,
                  },
                ].map((item, i) => (
                  <div key={i} className="col-md-4 col-6">
                    <div
                      className="p-3 rounded-4 border h-100"
                      style={{ background: "#fff", borderColor: "#e8eaed" }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 mb-2"
                        style={{
                          width: 32,
                          height: 32,
                          background: item.bg,
                          color: item.color,
                          fontSize: 13,
                        }}
                      >
                        {item.icon}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          fontWeight: 500,
                          marginBottom: 3,
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        className="fw-bold"
                        style={{
                          fontSize: 14,
                          color: item.valueColor ?? "#0f172a",
                          fontFamily: "'Sora', sans-serif",
                        }}
                        title={raffle?.typeData?.description_short}
                      >
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dates */}
              <p
                className="mb-2"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Datas Importantes
              </p>
              <div
                className="p-3 rounded-4 border mb-4"
                style={{ background: "#fff", borderColor: "#e8eaed" }}
              >
                <div className="row g-3">
                  {[
                    {
                      label: "Criada em",
                      date: raffle.createdAt,
                      accent: "#7c3aed",
                      show: true,
                    },
                    {
                      label: "Ativada em",
                      date: raffle.activationDate,
                      accent: "#16a34a",
                      show: !!raffle.activationDate,
                    },
                    {
                      label: "Ativação Automática",
                      date: raffle.autoActivationDate,
                      accent: "#0891b2",
                      show: !!raffle.autoActivationDate,
                    },
                    {
                      label: "Finalizada em",
                      date: raffle.finishedAt,
                      accent: "#f59e0b",
                      show: !!raffle.finishedAt,
                    },
                  ]
                    .filter((d) => d.show)
                    .map((d, i) => (
                      <div key={i} className="col-md-3 col-6">
                        <div
                          style={{
                            borderLeft: `3px solid ${d.accent}`,
                            paddingLeft: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              color: "#94a3b8",
                              fontWeight: 500,
                            }}
                          >
                            {d.label}
                          </div>
                          <div
                            className="fw-bold"
                            style={{
                              fontSize: 13,
                              color: "#0f172a",
                              fontFamily: "'Sora', sans-serif",
                            }}
                          >
                            {dayjs(d.date).format("DD/MM/YYYY")}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>
                            às {dayjs(d.date).format("HH:mm:ss")}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Description toggle */}
              <p
                className="mb-2"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Descrição
              </p>
              <div
                className="rounded-4 border overflow-hidden"
                style={{ borderColor: "#e8eaed", background: "#fff" }}
              >
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShow((p) => ({ ...p, description: !p.description }));
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ color: "#475569", fontSize: 13, fontWeight: 500 }}
                  >
                    <FaEye size={14} style={{ color: "#94a3b8" }} />
                    Descrição Completa
                  </div>
                  <button
                    type="button"
                    className="d-flex align-items-center gap-1 px-3 py-1 rounded-pill border"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      background: "#f8fafc",
                      color: "#475569",
                      borderColor: "#e2e8f0",
                      cursor: "pointer",
                    }}
                  >
                    {show.description ? (
                      <FaEyeSlash size={11} />
                    ) : (
                      <FaEye size={11} />
                    )}
                    {show.description ? "Ocultar" : "Mostrar"}
                  </button>
                </div>

                {show.description && (
                  <div
                    className="px-3 pb-3"
                    style={{
                      borderTop: "1px solid #e8eaed",
                      paddingTop: 14,
                      fontSize: 13,
                      color: "#64748b",
                      lineHeight: 1.7,
                      maxHeight: "20rem",
                      overflowY: "auto",
                    }}
                    dangerouslySetInnerHTML={{ __html: raffle.description }}
                  />
                )}
              </div>

              {!!raffle?.categories?.length && (
                <div className="mt-4">
                  <p
                    className="mb-2"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      color: "#94a3b8",
                    }}
                  >
                    Categorias
                  </p>
                  <div className="row g-2 justify-content-start">
                    {raffle.categories.map((category) => (
                      <div key={category.id} className="col-auto">
                        <span
                          className="badge bg-white text-dark border px-3 py-2"
                          title={
                            category.categoryData.description_short ||
                            category.categoryData.description ||
                            category.categoryData.name
                          }
                          style={{
                            borderColor: "#d8dee6",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {category.categoryData.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
};
